import {
	Container,
	ContainerPadding,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
	styled,
	Breakpoints,
	Layout,
	Switch,
	CTA,
	ContainerGutter,
	BreakpointWrapper,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { PortalContext } from '../../../../context/portal-context';
import { GetPersonUser } from '@esportal/portal-service';
import { useTrackingManager } from '../../../../tracking/use-tracking-manager';
import { formatOwnership } from '../../../../tracking/tracking-helper';
import { useErrorAlert } from '../../../../hooks/use-error-alert';
import { useSetUpdating } from '../../../../context/updating-context';

const StyledContainer = styled.section<{ isConfirmCancel: boolean }>`
	align-content: ${(props: { isConfirmCancel: any }) => !props.isConfirmCancel && 'center'};

	@media screen and (min-width: 560px) {
		min-height: ${(props: { isConfirmCancel: any }) => !props.isConfirmCancel && '90vh'};
	}

	@media screen and (min-width: 960px) {
		position: ${(props: { isConfirmCancel: any }) => !props.isConfirmCancel && 'absolute'};
		top: ${(props: { isConfirmCancel: any }) => !props.isConfirmCancel && 'var(--size-dynamic0130)'};
		padding-top: ${(props: { isConfirmCancel: any }) => props.isConfirmCancel && 'var(--size-dynamic0050)'};
	}
`;

export const ClubSettings: React.FC<{
	handleClose: (useTracking: boolean) => void;
}> = ({ handleClose }) => {
	const { portalService } = React.useContext(PortalContext);
	const setUpdating = useSetUpdating();
	const { showAlert } = useErrorAlert();

	const trackingManager = useTrackingManager();

	const userData = portalService.getPerson().getValue();

	const [switchNotificaciones, setSwitchNotificaciones] = React.useState(
		userData?.portalUser.data.clubVW.notificaciones,
	);
	const [disabledSwitch, setDisabledSwitch] = React.useState(false);
	const [confirmCancel, setConfirmCancel] = React.useState(false);

	const userVehicles = portalService.getPersonVehicles().getValue();

	const handleSwitch = () => {
		setDisabledSwitch(true);
		setSwitchNotificaciones(!switchNotificaciones);

		trackingManager.trackClubVWButtonClick(
			'Club VW communications switch',
			{
				contentId: `Toggle - Notifications ${switchNotificaciones === true ? 'deactivated' : 'activated'}`,
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);

		portalService.consentsPerson(!switchNotificaciones).subscribe({
			next: (data: GetPersonUser | null) => {
				if (data && userData) {
					userData.portalUser.data.clubVW.notificaciones = !switchNotificaciones;
				} else {
					setSwitchNotificaciones(!switchNotificaciones);
				}
				setDisabledSwitch(false);
			},
			error: () => {
				setSwitchNotificaciones(!switchNotificaciones);
				setDisabledSwitch(false);
			},
		});
	};

	const handleConfirmationOnClick = () => {
		setConfirmCancel(true);
		trackingManager.trackClubVWButtonClick(
			'Darme de baja',
			{ contentId: 'Unsubscribe from Club Volkswagen' },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
	};

	const handleConsultarOnClick = () => {
		trackingManager.trackClubVWLayerCloseClick('Club Volkswagen settings Layer', {
			contentId: 'Club Volkswagen settings Layer',
		});

		const link =
			'https://www.volkswagen.es/es/clientes/myvolkswagen.html?---=%7B%22clientes_myvolkswagen_featureAppSection%22%3A%22%2FuserSettings%22%7D';
		window.open(link, '_blank');
	};

	const handleConfirmUnsubscribeOnClick = () => {
		trackingManager.trackClubVWButtonClick(
			'Si, quiero darme de baja',
			{
				contentId: 'Confirm unsubscribe from Club Volkswagen',
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);

		setUpdating(true);
		handleClose(false);
		setTimeout(() => {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}, 100);

		portalService.unsuscribePerson().subscribe({
			next: () => {
				setUpdating(false);
			},
			error: () => {
				setUpdating(false);
				showAlert();
			},
		});
	};

	const handleCancelUnsubscribeOnClick = () => {
		trackingManager.trackClubVWButtonClick(
			'Prefiero seguir en el club',
			{
				contentId: 'Cancel unsubscribe from Club Volkswagen',
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);

		setConfirmCancel(false);
	};

	return (
		<StyledContainer className="club-settings__styled-container" isConfirmCancel={confirmCancel}>
			<Layout
				data-testid="club-settings__layout"
				appearance={{
					[Breakpoints.default]: [
						{ name: '.', columns: 1 },
						{ name: 'a', columns: 22 },
						{ name: '.', columns: 1 },
					],
					[Breakpoints.b1280]: [
						{ name: '.', columns: 1 },
						{ name: 'a', columns: 20 },
						{ name: '.', columns: 3 },
					],
					[Breakpoints.b1600]: [
						{ name: '.', columns: 2 },
						{ name: 'a', columns: 17 },
						{ name: '.', columns: 5 },
					],
					[Breakpoints.b1920]: [
						{ name: '.', columns: 3 },
						{ name: 'a', columns: 15 },
						{ name: '.', columns: 6 },
					],
					[Breakpoints.b2560]: [
						{ name: '.', columns: 5 },
						{ name: 'a', columns: 11 },
						{ name: '.', columns: 8 },
					],
				}}
			>
				{confirmCancel ? (
					<Container verticalAlign="center" padding={{ bottom: ContainerPadding.dynamic0250 }} stretchContent>
						<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
							<Text
								appearance={TokenTextAppearance.headline400}
								textAlign={TextAlignment.left}
								color={TokenTextColor.primary}
								bold
							>
								¿Seguro que quieres darte de baja?
							</Text>
						</BreakpointWrapper>
						<BreakpointWrapper min={Breakpoints.b960}>
							<Text
								appearance={TokenTextAppearance.headline300}
								textAlign={TextAlignment.left}
								color={TokenTextColor.primary}
								bold
							>
								¿Seguro que quieres darte de baja?
							</Text>
						</BreakpointWrapper>
						<Container padding={{ top: ContainerPadding.dynamic0100, bottom: ContainerPadding.dynamic0130 }}>
							<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.left}>
								Formar parte del club no tiene ningún coste y te permite disfrutar de beneficios exclusivos como
								facilidades en taller, atención al cliente prioritaria y pase VIP en nuestras experiencias.
							</Text>
						</Container>
						<Container
							wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
							gutter={ContainerGutter.dynamic0100}
							padding={{
								right: {
									[Breakpoints.b560]: ContainerPadding.grid006,
									[Breakpoints.b960]: ContainerPadding.grid002,
									[Breakpoints.b1280]: ContainerPadding.grid004,
									[Breakpoints.b1600]: ContainerPadding.grid002,
								},
							}}
							stretchContent
						>
							<CTA
								tag="button"
								emphasis="secondary"
								aria-label="Si, quiero darme de baja"
								size="large"
								onClick={handleConfirmUnsubscribeOnClick}
								stretchContent
							>
								<Text>Sí, quiero darme de baja</Text>
							</CTA>

							<CTA
								tag="button"
								emphasis="secondary"
								aria-label="Prefiero seguir en el club"
								size="large"
								onClick={handleCancelUnsubscribeOnClick}
								stretchContent
							>
								<Text>Prefiero seguir en el club</Text>
							</CTA>
						</Container>
					</Container>
				) : (
					<Container wrap={'always'}>
						{/* Container 1 */}
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0200,
									[Breakpoints.b960]: ContainerPadding.dynamic0100,
								},
							}}
						>
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<Text
									appearance={TokenTextAppearance.headline400}
									textAlign={TextAlignment.left}
									color={TokenTextColor.primary}
								>
									Ajustes de mi membresía del <Text bold>Club Volkswagen</Text>
								</Text>
							</BreakpointWrapper>
							<BreakpointWrapper min={Breakpoints.b960}>
								<Text
									appearance={TokenTextAppearance.headline300}
									textAlign={TextAlignment.left}
									color={TokenTextColor.primary}
								>
									Ajustes de mi membresía del <Text bold>Club Volkswagen</Text>
								</Text>
							</BreakpointWrapper>
							<Container
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0100,
										[Breakpoints.b960]: ContainerPadding.dynamic0050,
									},
								}}
							>
								<Text
									appearance={TokenTextAppearance.bigcopy100}
									textAlign={TextAlignment.left}
									color={TokenTextColor.primary}
								>
									Desde aquí, puedes definir tus preferencias en cuanto a comunicaciones asociadas al Club Volkswagen,
									así como solicitar la baja como miembro.
								</Text>
							</Container>
						</Container>

						{/* Container 2 */}
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0200,
									[Breakpoints.b960]: ContainerPadding.dynamic0100,
								},
							}}
							stretchContent
						>
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<Text
									appearance={TokenTextAppearance.headline300}
									textAlign={TextAlignment.left}
									color={TokenTextColor.primary}
								>
									Comunicaciones asociadas al Club Volkswagen
								</Text>
							</BreakpointWrapper>
							<BreakpointWrapper min={Breakpoints.b960}>
								<Text
									appearance={TokenTextAppearance.headline200}
									textAlign={TextAlignment.left}
									color={TokenTextColor.primary}
								>
									Comunicaciones asociadas al Club Volkswagen
								</Text>
							</BreakpointWrapper>
							<Container
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0050,
										[Breakpoints.b960]: ContainerPadding.dynamic0020,
									},
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0130,
										[Breakpoints.b960]: ContainerPadding.dynamic0100,
									},
								}}
							>
								<Text
									appearance={TokenTextAppearance.bigcopy100}
									textAlign={TextAlignment.left}
									color={TokenTextColor.primary}
								>
									Si lo deseas, puedes personalizar el envío de comunicaciones asociadas al club.
								</Text>
							</Container>

							<Container
								wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b560]: 'never' }}
								horizontalAlign={'space-between'}
								verticalAlign={'center'}
								shrinkContent
							>
								<Container
									padding={{
										right: {
											[Breakpoints.default]: ContainerPadding.grid003,
											[Breakpoints.b560]: ContainerPadding.grid001,
											[Breakpoints.b1280]: ContainerPadding.static0,
										},
										bottom: {
											[Breakpoints.default]: ContainerPadding.static200,
											[Breakpoints.b560]: ContainerPadding.static0,
										},
									}}
									shrinkContent
								>
									<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.left}>
										Recibir comunicaciones comerciales asociadas al Club Volkswagen
									</Text>
								</Container>

								<Switch onChange={handleSwitch} checked={switchNotificaciones} disabled={disabledSwitch}>
									{switchNotificaciones ? 'Activado' : 'Desactivado'}
								</Switch>
							</Container>
						</Container>

						{/* Container 3 */}
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0200,
									[Breakpoints.b960]: ContainerPadding.dynamic0100,
								},
							}}
							wrap={'always'}
						>
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.left}>
									Cancelar la membresía del Club Volkswagen
								</Text>
							</BreakpointWrapper>
							<BreakpointWrapper min={Breakpoints.b960}>
								<Text appearance={TokenTextAppearance.headline200} textAlign={TextAlignment.left}>
									Cancelar la membresía del Club Volkswagen
								</Text>
							</BreakpointWrapper>

							<Container
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0050,
										[Breakpoints.b960]: ContainerPadding.dynamic0020,
									},
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0130,
										[Breakpoints.b960]: ContainerPadding.dynamic0100,
									},
								}}
							>
								<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.left}>
									Si quieres darte de baja del Club Volkswagen, puedes hacerlo a continuación.
								</Text>
							</Container>
							<Container
								padding={{
									right: {
										[Breakpoints.b560]: ContainerPadding.grid006,
										[Breakpoints.b960]: ContainerPadding.grid011,
										[Breakpoints.b1600]: ContainerPadding.grid009,
										[Breakpoints.b1920]: ContainerPadding.grid007,
										[Breakpoints.b2560]: ContainerPadding.grid005,
									},
								}}
								stretchContent
							>
								<CTA
									tag="button"
									emphasis="secondary"
									aria-label="darme de baja"
									size="large"
									onClick={handleConfirmationOnClick}
									stretchContent
								>
									<Text>Darme de baja</Text>
								</CTA>
							</Container>
							<Container
								wrap={'always'}
								horizontalAlign={'center'}
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0100,
										[Breakpoints.b960]: ContainerPadding.dynamic0050,
									},
								}}
							>
								<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.left}>
									¿Necesitas ayuda? Contacta con nosotros a través del teléfono 800 500 100 o el email{' '}
									<CTA
										tag="a"
										emphasis="tertiary"
										href="https://www.volkswagen.es/es/volkswagen-espana/politica-privacidad.html"
										target="_blank"
									>
										<Text appearance={TokenTextAppearance.copy200}>atencionalcliente@volkswagen.es</Text>
									</CTA>
									. También puedes consultar los detalles sobre nuestra{' '}
									<CTA
										tag="a"
										emphasis="tertiary"
										href="https://www.volkswagen.es/es/volkswagen-espana/politica-privacidad.html"
										target="_blank"
										onClick={() => {
											trackingManager.trackClubVWLinkClick(
												'Política de privacidad',
												{
													contentId: 'Navigate to privacy policy',
												},
												{ vehicle: formatOwnership(userVehicles?.vehicles) },
											);
										}}
									>
										<Text appearance={TokenTextAppearance.copy200}>Política de Privacidad</Text>
									</CTA>
									.
								</Text>
							</Container>
						</Container>

						{/* Container 4 */}
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0250,
									[Breakpoints.b960]: ContainerPadding.dynamic0130,
								},
							}}
							wrap={'always'}
						>
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<Text appearance={TokenTextAppearance.headline400} textAlign={TextAlignment.left}>
									Perfil y ajustes de mi cuenta de <Text bold>Volkswagen ID.</Text>
								</Text>
							</BreakpointWrapper>
							<BreakpointWrapper min={Breakpoints.b960}>
								<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.left}>
									Perfil y ajustes de mi cuenta de <Text bold>Volkswagen ID.</Text>
								</Text>
							</BreakpointWrapper>

							<Container
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0100,
										[Breakpoints.b960]: ContainerPadding.dynamic0050,
									},
									bottom: {
										[Breakpoints.default]: ContainerPadding.dynamic0130,
										[Breakpoints.b960]: ContainerPadding.dynamic0100,
									},
								}}
							>
								<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.left}>
									Consulta los datos de tu cuenta Volkswagen ID y de tu área de usuario myVolkswagen desde aquí.
								</Text>
							</Container>
							<Container
								padding={{
									right: {
										[Breakpoints.b560]: ContainerPadding.grid006,
										[Breakpoints.b960]: ContainerPadding.grid011,
										[Breakpoints.b1600]: ContainerPadding.grid009,
										[Breakpoints.b1920]: ContainerPadding.grid007,
										[Breakpoints.b2560]: ContainerPadding.grid005,
									},
								}}
								stretchContent
							>
								<CTA
									tag="button"
									emphasis="primary"
									aria-label="consultar volkswagen ID"
									size="large"
									onClick={handleConsultarOnClick}
									stretchContent
								>
									<Text>Consultar</Text>
								</CTA>
							</Container>
						</Container>
					</Container>
				)}
			</Layout>
		</StyledContainer>
	);
};
